"use client";

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, RefreshCw, Trash2, CheckCircle, XCircle, Clock,
  MessageSquare, X, Loader2, ExternalLink
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

// ── Types ───────────────────────────────────────────────────────────

interface WhatsAppTemplate {
  id: string;
  template_name: string;
  content_sid: string | null;
  category: string;
  language: string;
  body_template: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  last_synced_at: string | null;
  created_at: string | null;
}

interface CreateTemplateForm {
  template_name: string;
  category: string;
  language: string;
  body_template: string;
  content_sid: string;
}

// ── Status badge ────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: string }) => {
  const config: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
    approved: { icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-100' },
    rejected: { icon: XCircle, color: 'text-red-700', bg: 'bg-red-100' },
    pending: { icon: Clock, color: 'text-yellow-700', bg: 'bg-yellow-100' },
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.color}`}>
      <Icon className="w-3 h-3" /> {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// ── Component ───────────────────────────────────────────────────────

export const WhatsAppTemplateManager: React.FC = () => {
  const tabbed = useTabbedChrome();
  const organizationId = localStorage.getItem('active_cbo_id') ? Number(localStorage.getItem('active_cbo_id')) : null;
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateTemplateForm>({
    template_name: '',
    category: 'marketing',
    language: 'en',
    body_template: '',
    content_sid: '',
  });

  const fetchTemplates = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const res = await authFetch(`${getWeaveinApiUrl()}/api/whatsapp-templates/?org_id=${organizationId}`);
      if (res.ok) setTemplates(await res.json());
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const createTemplate = async () => {
    if (!organizationId || !formData.template_name) return;
    try {
      const res = await authFetch(`${getWeaveinApiUrl()}/api/whatsapp-templates/`, {
        method: 'POST',
        jsonBody: { ...formData, organization_id: organizationId }
      });
      if (res.ok) {
        setShowCreateForm(false);
        setFormData({ template_name: '', category: 'marketing', language: 'en', body_template: '', content_sid: '' });
        fetchTemplates();
      }
    } catch (err) {
      console.error('Failed to create template:', err);
    }
  };

  const syncTemplate = async (id: string) => {
    setSyncing(id);
    try {
      await authFetch(`${getWeaveinApiUrl()}/api/whatsapp-templates/${id}/sync`, {
        method: 'POST'
      });
      fetchTemplates();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(null);
    }
  };

  const deleteTemplate = async (id: string) => {
    if (!confirm('Delete this template?')) return;
    try {
      await authFetch(`${getWeaveinApiUrl()}/api/whatsapp-templates/${id}`, {
        method: 'DELETE'
      });
      fetchTemplates();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            WhatsApp Templates
          </h2>
          )}
          <p className={`text-sm text-gray-500 ${tabbed ? '' : 'mt-1'}`}>
            Manage pre-approved WhatsApp message templates for outbound campaigns
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-v2accent text-white rounded-lg hover:bg-v2accent/90 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> Register Template
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-paper-2 border border-v2border rounded-lg p-4 text-sm text-ink-700">
        <p className="font-medium mb-1">WhatsApp Template Requirements</p>
        <p>
          Templates must be pre-approved by Meta/Twilio before they can be used for outbound messages
          outside the 24-hour session window. Create templates in the Twilio Console, then register
          the Content SID here to track approval status.
        </p>
      </div>

      {/* Template List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading templates...
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">No templates registered</p>
          <p className="text-sm mt-1">Register your first WhatsApp message template to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((tmpl) => (
            <div key={tmpl.id} className="bg-white border rounded-lg p-4 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900 text-sm">{tmpl.template_name}</span>
                  <StatusBadge status={tmpl.status} />
                  <span className="text-xs text-gray-400 px-2 py-0.5 bg-gray-100 rounded">
                    {tmpl.category}
                  </span>
                  <span className="text-xs text-gray-400">{tmpl.language}</span>
                </div>
                {tmpl.body_template && (
                  <p className="text-sm text-gray-600 truncate">{tmpl.body_template}</p>
                )}
                {tmpl.content_sid && (
                  <p className="text-xs text-gray-400 mt-1">Content SID: {tmpl.content_sid}</p>
                )}
                {tmpl.rejection_reason && (
                  <p className="text-xs text-red-600 mt-1">Rejection: {tmpl.rejection_reason}</p>
                )}
                {tmpl.last_synced_at && (
                  <p className="text-xs text-gray-400 mt-1">
                    Last synced: {new Date(tmpl.last_synced_at).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => syncTemplate(tmpl.id)}
                  disabled={syncing === tmpl.id || !tmpl.content_sid}
                  className="p-1.5 text-ink-400 hover:text-ink hover:bg-paper-2 rounded transition-colors disabled:opacity-30"
                  title="Sync status from Twilio"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing === tmpl.id ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => deleteTemplate(tmpl.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Register WhatsApp Template</h3>
              <button onClick={() => setShowCreateForm(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                <input
                  type="text"
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                  placeholder="e.g., appointment_reminder"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-v2accent focus:border-v2accent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-v2accent focus:border-v2accent"
                  >
                    <option value="marketing">Marketing</option>
                    <option value="utility">Utility</option>
                    <option value="authentication">Authentication</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-v2accent focus:border-v2accent"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="zh">Chinese</option>
                    <option value="vi">Vietnamese</option>
                    <option value="ko">Korean</option>
                    <option value="tl">Tagalog</option>
                    <option value="fr">French</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Twilio Content SID <span className="text-gray-400 font-normal">(if already created in Twilio)</span>
                </label>
                <input
                  type="text"
                  value={formData.content_sid}
                  onChange={(e) => setFormData({ ...formData, content_sid: e.target.value })}
                  placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-v2accent focus:border-v2accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body Template</label>
                <textarea
                  value={formData.body_template}
                  onChange={(e) => setFormData({ ...formData, body_template: e.target.value })}
                  rows={4}
                  placeholder="Hi {{1}}, your appointment is on {{2}}. Reply YES to confirm."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-v2accent focus:border-v2accent"
                />
                <p className="text-xs text-gray-400 mt-1">Use {'{{1}}'}, {'{{2}}'}, etc. for template variables</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createTemplate}
                disabled={!formData.template_name}
                className="px-4 py-2 text-sm bg-v2accent text-white rounded-lg hover:bg-v2accent/90 disabled:opacity-50"
              >
                Register Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplateManager;
